/**
 * Valeurs fermées partagées entre le serveur et le frontend.
 *
 * Ce fichier est la source de vérité unique : les enums PostgreSQL (apps/server/src/db/schema.ts)
 * sont déclarés à partir de ces tuples, et le frontend les réutilise pour ses libellés.
 */
export const USER_ROLES = ['admin', 'operator', 'viewer'];
/** Hiérarchie des rôles : un rôle donne accès à tout ce que permettent les rôles inférieurs. */
export const ROLE_RANK = { viewer: 0, operator: 1, admin: 2 };
export const INSTALL_TYPES = [
    'ghost_cli_systemd',
    'ghost_cli_local',
    'docker_compose',
    'custom',
];
export const DB_TYPES = ['mysql', 'sqlite'];
export const UPDATE_POLICIES = ['manual', 'minor_auto', 'all_auto'];
/**
 * Qui redémarre le site.
 *
 * `driver` : Vigie s'en charge, par le moyen du type d'installation. `external` : le redémarrage
 * se fait hors de Vigie, dans le panneau de l'hébergeur — cas des hébergements où l'application
 * Node.js est supervisée par le fournisseur, et où aucune commande SSH ne peut la relancer.
 */
/**
 * Ce qu'on vient faire en ajoutant un site.
 *
 * `existant` : adopter une installation déjà en place. `neuf` : la faire installer par Vigie. Le
 * choix est demandé d'emblée parce qu'il change les questions posées, les prérequis à vérifier et
 * ce qui est bloquant — mais pas l'enregistrement, commun aux deux.
 */
export const ONBOARDING_INTENTIONS = ['existant', 'neuf'];
export const RESTART_MODES = ['driver', 'external'];
export const BACKUP_METHODS = ['restic', 'streamed'];
export const BACKUP_TYPES = ['scheduled', 'manual', 'pre_update'];
export const BACKUP_STATUSES = ['running', 'success', 'failed'];
export const SITE_STATUSES = ['up', 'degraded', 'down', 'unknown'];
export const CONFIG_SOURCES = ['json_file', 'compose_env', 'env_file'];
export const STORAGE_SCOPES = ['global', 'organization', 'site'];
export const SITE_OPERATIONS = ['backup', 'update', 'restore', 'config_apply'];
/**
 * Étapes de l'installation d'un site neuf, de l'instance nue au site en ligne.
 *
 * Sans accents, contrairement au reste du domaine : ces valeurs deviennent un type PostgreSQL, se
 * lisent dans des URL, servent de classes CSS et se cherchent au `grep` dans des journaux. Les
 * libellés en français vivent dans l'interface, où ils ont leur place.
 *
 * `dns` est une **attente humaine** : les enregistrements ne peuvent être posés que chez le bureau
 * d'enregistrement, et rien ne doit clore cet état — ni un délai, ni la réconciliation au démarrage.
 * Une installation qui attend son DNS depuis trois jours attend toujours, et c'est correct.
 */
export const INSTALLATION_STATUSES = [
    'en_file',
    'preparation',
    'docker',
    'bordure',
    'pile',
    'ghost',
    'enregistrement',
    'dns',
    'terminee',
    'echouee',
    'interrompue',
];
/**
 * Mode d'installation.
 *
 * `essai` sert au harnais de tests : la bordure y sert en clair sur un port de la boucle locale, et
 * la vérification DNS est sautée — un conteneur n'a ni nom public ni certificat. Il est refusé en
 * production, faute de quoi une erreur de manipulation servirait un vrai site sans TLS.
 */
export const INSTALLATION_MODES = ['production', 'essai'];
/**
 * Formats de rendu d'un canal d'alerte.
 *
 * `vigie` transmet la charge utile documentée dans docs/alertes.md, signée. `slack` la rend en
 * message lisible : un webhook entrant Slack n'accepte pas de JSON arbitraire — il attend `text` ou
 * des blocs, et refuse tout le reste par `invalid_payload`. Sans ce format, brancher une URL Slack
 * produirait un canal qui échoue à chaque alerte.
 */
export const WEBHOOK_FORMATS = ['vigie', 'slack'];
export const ALERT_TYPES = [
    'site_down',
    'disk_high',
    'backup_failed',
    'update_failed',
    'major_available',
    'ssh_host_key_mismatch',
    /**
     * Mise à jour installée, en attente d'un redémarrage qui n'appartient pas à Vigie.
     *
     * Une alerte et non un simple statut : sur ces hébergements, personne ne saurait qu'il reste un
     * geste à faire, et le site tournerait sur l'ancienne version en croyant la nouvelle installée.
     */
    'update_awaiting_restart',
];
/** Catégories d'erreur normalisées : les modules réagissent au type, jamais au message. */
export const DRIVER_ERROR_CATEGORIES = [
    'unreachable',
    'permission_denied',
    'binary_missing',
    'timeout',
    'restricted_env',
    'command_failed',
    'unsupported',
];
/**
 * États de la machine à états de mise à jour.
 *
 * `pending_approval` couvre le cas « majeure détectée » : une majeure n'est jamais appliquée
 * automatiquement, y compris sous la politique all_auto (arbitrage client du 2026-08-18).
 * `interrupted` est posé par la réconciliation au démarrage quand un run a un heartbeat périmé.
 */
export const UPDATE_STATUSES = [
    'pending_approval',
    'queued',
    'prechecks',
    'backing_up',
    'updating',
    /**
     * Fichiers installés, redémarrage à faire hors de Vigie.
     *
     * L'attente est **humaine** : le run ne progresse plus tant que personne n'a redémarré
     * l'application dans le panneau de l'hébergeur et validé l'avoir fait. Il ne tient donc plus le
     * verrou du site, et la réconciliation au démarrage ne le déclare pas interrompu.
     */
    'awaiting_restart',
    'verifying',
    'success',
    'failed',
    'rolled_back',
    'interrupted',
];
/** États terminaux : aucune transition sortante, rien à réconcilier au démarrage. */
export const TERMINAL_UPDATE_STATUSES = [
    'success',
    'failed',
    'rolled_back',
];
/**
 * Clés de configuration Ghost dont la valeur est un secret.
 *
 * Les **noms d'utilisateur en sont volontairement absents**. Un identifiant n'est pas un secret :
 * le masquer priverait du diagnostic le plus utile de l'éditeur — « Ghost se connecte-t-il avec le
 * bon compte ? » — sans rien protéger, puisqu'il apparaît de toute façon dans les journaux et la
 * table des process. Ce qu'il faut cacher, c'est le mot de passe qui va avec.
 *
 * Cette liste est complétée par une heuristique sur le nom de la clé (pass, secret, token, key…),
 * qui attrape ce que Ghost ou un thème ajoutent sans qu'on l'ait prévu.
 */
export const SENSITIVE_CONFIG_KEY_PATTERNS = [
    'mail.options.auth.pass',
    'database.connection.password',
    /*
     * Les clés des services externes de Ghost 6.
     *
     * L'heuristique sur le nom les attrape déjà (`apiKey`, `token`, `adminToken`), mais les nommer
     * ici les met à l'abri d'une évolution de cette heuristique : un secret manqué est un secret
     * diffusé, dans un diff, un journal ou une capture d'écran de support.
     */
    'bulkEmail.mailgun.apiKey',
    'tinybird.adminToken',
    'tinybird.tracker.token',
    'tinybird.tracker.local.token',
    'tinybird.stats.token',
    'tinybird.stats.local.token',
];
//# sourceMappingURL=constants.js.map